import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import type { ComponentType } from "react";
import {
  GiAbacus,
  GiAncientColumns,
  GiBookCover,
  GiBookmark,
  GiChaliceDrops,
  GiChurch,
  GiCloudRing,
  GiFlowerPot,
  GiFruitTree,
  GiHelmet,
  GiHolySymbol,
  GiMountainCave,
  GiMusicalScore,
  GiOakLeaf,
  GiOpenBook,
  GiPaintBrush,
  GiPawPrint,
  GiPolarStar,
  GiScrollQuill,
  GiScrollUnfurled,
  GiSeagull,
  GiSparkles,
  GiStoneTablet,
  GiThreeFriends,
  GiTowerBridge,
} from "react-icons/gi";

type GameCard = {
  slug: string;
  title: string;
  band: string;
  subject: string;
  summary: string;
  Icon: ComponentType<{ className?: string }>;
  available: boolean;
};

const GAMES: GameCard[] = [
  {
    slug: "latin-root-garden",
    title: "The Latin Root Garden",
    band: "Seeds · ages 5–7",
    subject: "God of Words",
    summary:
      "Drag English words to the Latin roots they grew from. Watch a tree fill with leaves of meaning.",
    Icon: GiFruitTree,
    available: true,
  },
  {
    slug: "story-stones",
    title: "Story Stones",
    band: "Seeds · ages 5–7",
    subject: "God of Story",
    summary: "Place the stones of Genesis through the Gospels in order — and the path leads home.",
    Icon: GiAncientColumns,
    available: true,
  },
  {
    slug: "decline-this",
    title: "Decline This!",
    band: "Roots · ages 8–10",
    subject: "God of Words",
    summary:
      "Latin endings from the Vulgate and the Mass — pick the right case and a Roman sentence falls into place.",
    Icon: GiOpenBook,
    available: true,
  },
  {
    slug: "pattern-parade",
    title: "Pattern Parade",
    band: "Seeds · ages 5–7",
    subject: "God of Numbers",
    summary: "A parade of woodland creatures marches in patterns. Tap the next animal to keep them in step.",
    Icon: GiAbacus,
    available: true,
  },
  {
    slug: "sequence-songs",
    title: "Sequence Songs",
    band: "Roots · ages 8–10",
    subject: "God of Beauty",
    summary: "Hear a fragment of a real hymn — Te Deum, Salve Regina, Pange Lingua — then play it back on a row of piano keys. Real chant memory, by ear.",
    Icon: GiMusicalScore,
    available: true,
  },
  {
    slug: "constellation-quest",
    title: "Constellation Quest",
    band: "Roots · ages 8–10",
    subject: "God of Nature",
    summary: "Spot constellations on the night sky and learn the stories the ancients kept.",
    Icon: GiPolarStar,
    available: true,
  },
  {
    slug: "virtue-garden",
    title: "Virtue Garden",
    band: "Seeds · ages 5–7",
    subject: "God of Identity",
    summary: "Hear a small story about a child like you, then name the virtue you see growing.",
    Icon: GiFlowerPot,
    available: true,
  },
  {
    slug: "greek-roots",
    title: "Greek Roots",
    band: "Roots · ages 8–10",
    subject: "God of Words",
    summary: "Match each Greek word with its meaning. Watch ancient Greek surface inside dozens of English words you already know.",
    Icon: GiAncientColumns,
    available: true,
  },
  {
    slug: "hebrew-names",
    title: "Hebrew Names",
    band: "Roots · ages 8–10",
    subject: "God of Story",
    summary: "Every name in scripture means something — sometimes the whole person's story in one word. Match each name with its meaning.",
    Icon: GiScrollQuill,
    available: true,
  },
  {
    slug: "saint-symbols",
    title: "Saint Symbols",
    band: "Seeds · ages 6–9",
    subject: "God of Story",
    summary: "Every saint has a symbol you'll see in stained-glass windows. Match each saint with the symbol the Church remembers them by.",
    Icon: GiHolySymbol,
    available: true,
  },
  {
    slug: "apostle-find",
    title: "Find the Apostle",
    band: "Roots · ages 8–10",
    subject: "God of Story",
    summary: "The Twelve walked with Christ, then went to the ends of the earth. Match each apostle with what the Gospels remember him for.",
    Icon: GiThreeFriends,
    available: true,
  },
  {
    slug: "tree-by-leaf",
    title: "Tree by Leaf",
    band: "Seeds · ages 6–9",
    subject: "God of Nature",
    summary: "Every tree wears its name on its leaves. Match each tree with the leaf shape that gives it away.",
    Icon: GiOakLeaf,
    available: true,
  },
  {
    slug: "cloud-reading",
    title: "Cloud Reading",
    band: "Seeds · ages 6–9",
    subject: "God of Nature",
    summary: "Every cloud has a name. Once you know them, the sky tells you what the weather is about to do.",
    Icon: GiCloudRing,
    available: true,
  },
  {
    slug: "holy-land-cities",
    title: "Holy Land Cities",
    band: "Roots · ages 8–10",
    subject: "God of Story",
    summary: "Six cities of the Holy Land, each one a chapter in the long story. Match each with what happened there.",
    Icon: GiTowerBridge,
    available: true,
  },
  {
    slug: "cathedral-parts",
    title: "Parts of a Cathedral",
    band: "Roots · ages 8–10",
    subject: "God of Beauty",
    summary: "Every cathedral was built to teach. Every part has a name. Match each part with what it is.",
    Icon: GiChurch,
    available: true,
  },
  {
    slug: "sacrament-match",
    title: "Match the Sacrament",
    band: "Roots · ages 8–10",
    subject: "God of Story",
    summary: "The seven sacraments are visible signs of invisible grace. Match each with the sign you see when it is celebrated.",
    Icon: GiChaliceDrops,
    available: true,
  },
  {
    slug: "liturgical-colors",
    title: "Liturgical Colors",
    band: "Seeds · ages 6–9",
    subject: "God of Beauty",
    summary: "The Church wears different colors in different seasons. Match each season with the color of its vestments.",
    Icon: GiPaintBrush,
    available: true,
  },
  {
    slug: "bird-by-song",
    title: "Bird by Song",
    band: "Seeds · ages 6–9",
    subject: "God of Nature",
    summary: "A wooded morning is full of voices. Match each bird with the sound it sings.",
    Icon: GiSeagull,
    available: true,
  },
  {
    slug: "animal-tracks",
    title: "Animal Tracks",
    band: "Seeds · ages 6–9",
    subject: "God of Nature",
    summary: "Every animal leaves a different mark in soft ground. Match each with the print it leaves behind.",
    Icon: GiPawPrint,
    available: true,
  },
  {
    slug: "saint-pairs",
    title: "Saint Pairs",
    band: "Seeds · ages 6–9",
    subject: "God of Story",
    summary: "A grid of facedown cards. Flip two at a time and find each saint with the symbol the Church remembers them by.",
    Icon: GiHolySymbol,
    available: true,
  },
  {
    slug: "hymn-pairs",
    title: "Hymn Pairs",
    band: "Seeds · ages 6–9",
    subject: "God of Beauty",
    summary: "Match each Latin hymn with what its title means — six songs the Church has sung for centuries.",
    Icon: GiMusicalScore,
    available: true,
  },
  {
    slug: "star-pairs",
    title: "Star Pairs",
    band: "Seeds · ages 6–9",
    subject: "God of Nature",
    summary: "Six constellations and the names the ancients gave them. Pair the Latin name with what it means.",
    Icon: GiPolarStar,
    available: true,
  },
  {
    slug: "bible-order",
    title: "The Whole Bible in Ten Steps",
    band: "Roots · ages 8–10",
    subject: "God of Story",
    summary: "Ten moments hold the whole arc of the Bible. Tap them in order — Creation, the Fall, Abraham, the Exodus, the Resurrection, Pentecost.",
    Icon: GiBookmark,
    available: true,
  },
  {
    slug: "liturgy-order",
    title: "The Mass in Order",
    band: "Roots · ages 8–10",
    subject: "God of Beauty",
    summary: "The Mass has the same shape every time. Tap each part in its proper order, from greeting to final blessing.",
    Icon: GiChurch,
    available: true,
  },
  {
    slug: "creed-order",
    title: "The Apostles' Creed in Order",
    band: "Roots · ages 8–10",
    subject: "God of Story",
    summary: "One of the oldest creeds in the Church, said for nearly two thousand years. Tap the lines in the order they're prayed.",
    Icon: GiScrollUnfurled,
    available: true,
  },
  {
    slug: "roman-numerals",
    title: "Roman Numeral Builder",
    band: "Roots · ages 8–10",
    subject: "God of Numbers",
    summary: "Tap I, V, X, L, C, D, M tiles to build the number on the parchment. Eight rounds — from IV to the Battle of Hastings.",
    Icon: GiAbacus,
    available: true,
  },
  {
    slug: "latin-verbs",
    title: "Latin Verbs",
    band: "Roots · ages 8–10",
    subject: "God of Words",
    summary: "Eight Latin verbs in their first-person form. Match each with what it means in English.",
    Icon: GiOpenBook,
    available: true,
  },
  {
    slug: "body-latin",
    title: "The Body, in Latin",
    band: "Seeds · ages 6–9",
    subject: "God of Words",
    summary: "The Latin words for parts of the body live inside a hundred English words. Match each Latin word with the English part it names.",
    Icon: GiBookCover,
    available: true,
  },
  {
    slug: "ten-commandments",
    title: "The Ten Commandments in Order",
    band: "Roots · ages 8–10",
    subject: "God of Story",
    summary: "Given to Moses on Sinai, written by the finger of God on stone tablets. Tap them in the order they were given.",
    Icon: GiStoneTablet,
    available: true,
  },
  {
    slug: "beatitudes-order",
    title: "The Beatitudes in Order",
    band: "Roots · ages 8–10",
    subject: "God of Story",
    summary: "Christ's first sermon, on the mountain. Tap the eight Beatitudes in the order He spoke them.",
    Icon: GiOpenBook,
    available: true,
  },
  {
    slug: "gifts-of-spirit",
    title: "Gifts of the Holy Spirit",
    band: "Roots · ages 8–10",
    subject: "God of Identity",
    summary: "Seven gifts the Holy Spirit gives at Confirmation. Match each gift with what it does in a Christian's life.",
    Icon: GiSparkles,
    available: true,
  },
  {
    slug: "fruits-of-spirit",
    title: "Fruits of the Spirit",
    band: "Seeds · ages 6–9",
    subject: "God of Identity",
    summary: "Eight fruits of the Spirit, each paired with a tiny picture that captures it. Flip the cards and find each pair.",
    Icon: GiFruitTree,
    available: true,
  },
];

export function Landing() {
  return (
    <div className="above-paper container max-w-5xl py-12 sm:py-20">
      <header className="space-y-4 text-center">
        <p className="text-sm uppercase tracking-[0.25em] text-ink-muted">Schola Games</p>
        <h1 className="font-display text-4xl font-semibold text-balance text-navy sm:text-6xl">
          Classical learning, joyfully played.
        </h1>
        <p className="mx-auto max-w-2xl text-balance text-base text-ink-light sm:text-lg">
          A growing garden of games drawn from the Schola curriculum — Latin roots, sacred
          stories, the wonders of nature, the music of the spheres, the names of the
          virtues. Free to play, gentle to fail at, and made for families to revisit.
        </p>
        <div className="flex flex-wrap justify-center gap-3 pt-2">
          <Link
            to="/play/latin-root-garden"
            className="gold-pill min-h-[48px] text-base font-display !text-navy transition-transform hover:scale-[1.02] active:scale-[0.99]"
          >
            <GiFruitTree className="h-5 w-5 text-moss-deep" aria-hidden />
            Begin in the Garden
          </Link>
          <a
            href="https://theschola.app"
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full border-2 border-ink/30 bg-parchment-50/60 px-4 py-1.5 text-sm font-medium text-ink hover:bg-parchment-50"
          >
            About the curriculum →
          </a>
        </div>
      </header>

      {/* Featured: Michael and the Mountain */}
      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mt-12 sm:mt-16"
      >
        <Link
          to="/quest"
          className="ink-border block rounded-2xl bg-navy p-6 text-parchment-50 shadow-soft transition-transform hover:scale-[1.005] sm:p-8"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at top right, hsl(218 50% 26%) 0%, hsl(218 45% 18%) 60%, hsl(220 60% 10%) 100%)",
          }}
        >
          <div className="flex items-start gap-4 sm:gap-6">
            <div className="hidden flex-none sm:block">
              <GiMountainCave className="h-20 w-20 text-gold-light" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-[11px] font-medium uppercase tracking-[0.2em] text-gold-light">
                <GiSparkles className="h-3.5 w-3.5" aria-hidden />
                <span>An adventure in five acts</span>
              </div>
              <h2 className="mt-2 font-display text-2xl font-semibold sm:text-4xl">
                Michael and the Mountain
              </h2>
              <p className="mt-2 text-sm text-parchment-50/80 sm:text-base">
                Walk with Michael — a small squire at the foot of a great mountain — through
                fifteen realms of the curriculum on his ecumenical journey to be made a
                knight. Made in the lineage of Zelda, scored in 8-bit chiptune, telling the
                whole story of salvation as a knight's quest.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-gold/90 px-4 py-1.5 text-sm font-medium text-navy">
                <GiHelmet className="h-4 w-4" aria-hidden />
                Begin the Quest →
              </div>
            </div>
          </div>
        </Link>
      </motion.section>

      <div className="mt-12 text-center text-sm uppercase tracking-[0.25em] text-ink-muted sm:mt-16">
        Or pick a short game
      </div>

      <section className="mt-6 grid gap-5 sm:grid-cols-2">
        {GAMES.map((g, i) => (
          <motion.article
            key={g.slug}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 * i, duration: 0.5 }}
            className="ink-border wash flex flex-col gap-3 rounded-2xl p-5 sm:p-6"
          >
            <div className="flex items-start justify-between gap-3">
              <g.Icon className="h-10 w-10 text-navy sm:h-12 sm:w-12" aria-hidden />
              <div className="text-right text-[11px] font-medium uppercase tracking-wider text-ink-muted">
                <div>{g.band}</div>
                <div>{g.subject}</div>
              </div>
            </div>
            <h2 className="font-display text-xl font-semibold text-navy sm:text-2xl">
              {g.title}
            </h2>
            <p className="text-sm text-ink-light sm:text-base">{g.summary}</p>
            <div className="mt-2">
              {g.available ? (
                <Link
                  to={`/play/${g.slug}`}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-navy px-4 py-1.5 text-sm font-medium text-parchment-50 hover:bg-navy-light"
                >
                  Play →
                </Link>
              ) : (
                <span className="inline-flex items-center gap-2 rounded-full border border-ink/20 bg-parchment-100/70 px-4 py-1.5 text-sm font-medium text-ink-muted">
                  Coming soon
                </span>
              )}
            </div>
          </motion.article>
        ))}
      </section>

      <footer className="mt-16 border-t border-ink/15 pt-8 text-center text-sm text-ink-muted sm:mt-20">
        <p>
          Made for families, drawn from the{" "}
          <a
            href="https://theschola.app"
            target="_blank"
            rel="noreferrer"
            className="underline underline-offset-4 hover:text-ink"
          >
            Schola
          </a>{" "}
          classical curriculum.
        </p>
        <p className="mt-2 text-xs italic">More games are growing — check back through the year.</p>
      </footer>
    </div>
  );
}
